// The WebGL2 instance buffer: one `Float32Array`, one VBO and one VAO of
// divisor-1 attributes (the plan's A.2 and A.4).
//
// It used to live beside the quad batch. It sits here because the batch is
// backend-neutral now (`gfx/quads.ts`) and this half is not: it is a vertex
// array object and a `drawArraysInstanced`, and WebGPU has neither.
//
// **Location 0 belongs to the corner** (R553). Every instance attribute
// starts at location 1, because an instanced array at location 0 is what
// Firefox on a Mac emulates.
//
// It survives a context loss: the CPU array of `gfx/instance-array.ts` is
// kept, and `create` builds the GPU objects again from it.

import type { GL, GpuResource } from './context';
import { bindCorner, corners, type CornerBuffer } from './corner';
import { InstanceArray, STRIP_VERTICES } from '../gfx/instance-array';
import type { AttribSpec, InstanceBuffer as InstanceBufferApi } from '../gfx/types';

export class InstanceBuffer extends InstanceArray implements GpuResource, InstanceBufferApi {
  private vbo: WebGLBuffer | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private readonly corner: CornerBuffer;

  constructor(
    private readonly glc: GL,
    floatsPerInstance: number,
    private readonly attribs: readonly AttribSpec[],
    capacity: number,
  ) {
    super(floatsPerInstance, capacity);
    // The corner buffer registers first, so a restore builds it before this
    // vertex array binds it (R553).
    this.corner = corners(glc);
    glc.register(this);
  }

  create(gl: WebGL2RenderingContext): void {
    this.destroy(gl);
    const vbo = gl.createBuffer();
    const vao = gl.createVertexArray();
    if (!vbo || !vao) throw new Error('fast: cannot create an instance buffer');
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    const stride = this.floatsPerInstance * 4;
    for (const a of this.attribs) {
      gl.enableVertexAttribArray(a.location);
      gl.vertexAttribPointer(a.location, a.size, gl.FLOAT, false, stride, a.offset * 4);
      gl.vertexAttribDivisor(a.location, 1);
    }
    // The corner is the one PER-VERTEX attribute, and it takes location 0.
    bindCorner(gl, this.corner.buffer);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.vbo = vbo;
    this.vao = vao;
  }

  destroy(gl: WebGL2RenderingContext): void {
    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.vao) gl.deleteVertexArray(this.vao);
    this.vbo = null;
    this.vao = null;
  }

  protected rebuild(): void {
    if (!this.glc.lost) this.create(this.glc.gl);
  }

  /**
   * Uploads the written instances and draws them as one instanced strip.
   * Returns the instance count, or 0 when there was nothing to draw.
   */
  draw(vertexCount: number = STRIP_VERTICES): number {
    if (this.count === 0 || !this.vao || !this.vbo || this.glc.lost) return 0;
    const gl = this.glc.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.data, 0, this.count * this.floatsPerInstance);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, vertexCount, this.count);
    gl.bindVertexArray(null);
    this.glc.drawCalls++;
    this.glc.instances += this.count;
    return this.count;
  }

  dispose(): void { this.glc.unregister(this); }
}
